"use client"

import { useCallback, useEffect } from "react"
import { useBlockProfilesStore, useBlockUsersStore } from "./useBlockUsersStore"
import fetchClientMyBlockUsers from "./fetchClientMyBlockUsers"
import * as E from "fp-ts/Either"
import { pipe } from "fp-ts/function"
import fetchClientBlockUserProfiles from "./fetchClientBlockUserProfiles"
import { hasAccessToken } from "@/utils/cookieUtils"

export const useMyBlockUsers = (enabled: boolean = true) => {
  const { profiles, setProfiles, deleteProfile, clearProfiles } =
    useBlockProfilesStore()
  const {
    blockUsers,
    setBlockUsers,
    setIsLoading,
    isLoading,
    addBlockUser,
    deleteBlockUser,
  } = useBlockUsersStore()

  const fetch = useCallback(async () => {
    setIsLoading(true)
    try {
      const result = await fetchClientMyBlockUsers()
      pipe(
        result,
        E.match(
          () => {
            setBlockUsers([])
          },
          (data) => {
            setBlockUsers(data)
          },
        ),
      )
    } catch {
      setBlockUsers([])
    } finally {
      setIsLoading(false)
    }
  }, [setBlockUsers, setIsLoading])

  useEffect(() => {
    if (!enabled) return
    if (hasAccessToken()) fetch()
  }, [enabled, fetch])

  // profiles를 비동기로 가져오는 함수
  const getProfiles = useCallback(async () => {
    // store에 profiles가 없는 경우
    if (!profiles || profiles.length === 0) {
      console.log("Profiles not in store, fetching...")

      // blockUsers에서 profileId 목록 추출
      const profileIds = blockUsers.map((user) => user.profileId)

      if (profileIds.length > 0) {
        try {
          // 특정 fetch 함수 호출 (예시)
          const result = await fetchClientBlockUserProfiles(profileIds)

          pipe(
            result,
            E.match(
              (error) => {
                console.error("Failed to fetch profiles:", error)
                setProfiles([])
              },
              (data) => {
                console.log("Profiles fetched successfully:", data)
                // profiles에 blockedAt 필드 추가
                const profilesWithBlockedAt = data.map((profile) => {
                  // 해당 profile의 blockUser 찾기
                  const blockUser = blockUsers.find(
                    (user) => user.profileId === profile.id,
                  )
                  return {
                    ...profile,
                    blockedAt: blockUser?.createdAt || null,
                  }
                })
                setProfiles(profilesWithBlockedAt)
                return profilesWithBlockedAt
              },
            ),
          )
        } catch (error) {
          console.error("Error fetching profiles:", error)
          setProfiles([])
        }
      }
    }

    // 이미 store에 profiles가 있는 경우에도 blockedAt 추가
    const profilesWithBlockedAt = (profiles || []).map((profile) => {
      const blockUser = blockUsers.find((user) => user.profileId === profile.id)
      return {
        ...profile,
        blockedAt: blockUser?.createdAt || null,
      }
    })

    return profilesWithBlockedAt
  }, [blockUsers, profiles, setProfiles])

  // 차단 해제 함수
  const deleteBlockUserById = useCallback(
    (profileId: string) => {
      // blockUsers에서 해당 profileId를 가진 blockUser 찾기
      const blockUser = blockUsers.find((user) => user.profileId === profileId)

      if (blockUser) {
        // blockUsers store에서 삭제
        deleteBlockUser(blockUser.id)

        // profiles store에서도 삭제
        deleteProfile(profileId)

        console.log("Deleted block user:", blockUser.id, "profile:", profileId)
      }
    },
    [blockUsers, deleteBlockUser, deleteProfile],
  )

  return {
    blockUsers,
    fetch, // 차단 유저 목록 fetch 함수
    getProfiles, // async 함수로 profiles 가져오기
    addBlockUser,
    deleteBlockUser: deleteBlockUserById, // 차단 해제 함수
    clearProfiles, // profiles 초기화 함수
    isLoading,
  }
}

export default useMyBlockUsers
